import {useEffect, useState} from "react";
import {FoodEntry, FoodType, NutrientGroup} from "./domain/FoodEntry";
import {Link} from "react-router-dom";
import {isAdmin} from "./App";
import {mapGroupToClazz} from "./edit/NutrientTableHelper";

export function NutrientView() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [servingSize, setServingSize] = useState<number>(1);
    const [foodData, setFoodData] = useState<FoodEntry | undefined>(undefined);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const nutriId = urlParams.get('id');
        if(!nutriId || nutriId === "") {
            setLoading(false);
            setError("No nutrient id provided");
            return;
        }

        fetch(`/api/v1/foodEntry/id/${nutriId}`, {
            method: "GET",
            headers: {"Content-Type": "application/json", "Accept": "application/json"},
        }).then((response) => {
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.json(); // Parse JSON response
        }).then((jsonData) => {
            setFoodData(FoodEntry.fromJson(jsonData));
            setLoading(false); // Turn off loading spinner

        }).catch((err) => {
            setError(err.message); // Handle errors
            setLoading(false);
        });
    }, []);

    if(loading || !foodData) return <div>Loading Nutrient Data...</div>;
    if(error) return <div>Error: {error}</div>;

    function linkToEdit(foodType: any) {
        if(!foodData) return null;
        if(foodType === FoodType.COMPOSITE_INGREDIENT) {
            return <Link to={`/combinedFood?id=${foodData.id}&mode=edit`}>Edit this nutrient</Link>;
        }
        return <Link to={`/foodEntry?id=${foodData.id}&mode=edit`}>Edit this nutrient</Link>;
    }

    return <div className="ingredient-list">
        <div style={ { "flex": "1", "paddingBottom": "10px" }}>
            <h1>{foodData.name}</h1>
            <label>Serving Size:
                <select value={servingSize} onChange={e => setServingSize(parseFloat(e.target.value))}>
                    <option key="-1" value="1">Per 100g</option>
                    {foodData.portions.map(p =>
                        <option key={p.portionSizeId} value={p.gramQuantity / 100.0}>{p.portionSizeName}</option>
                    )}
                </select>
            </label>
            <h2>General information</h2>
            <div className="nutrient-details">
                <p><strong>Last Modified:</strong> {foodData.lastUpdated.toLocaleString()} by <em>{foodData.userName}</em></p>
                <p><strong>Publication Date:</strong> {foodData.publicationDate.toLocaleDateString()}</p>
                <p><strong>Data Source:</strong> {foodData.foodSource.asWebLink()}</p>
                <p><strong>Food Type:</strong> {foodData.foodType.name}</p>
                <p><strong>Category:</strong> {foodData.category}</p>
                <p><strong>NutriID:</strong> {foodData.id}</p>
                <p><strong>External ID:</strong> {foodData.externalId}</p>
            </div>
            {isAdmin() ? linkToEdit(foodData.foodType) : ""}
        </div>

        <CoreFoodEntryViewer foodData={foodData} servingSize={servingSize}/>
    </div>;
}

/**
 * This is a core food entry viewer that presents all the nutrients in the well known table format.
 * @param foodData {FoodEntry} the food entry to be presented
 * @param servingSize {number} the multiplier where 100 gram is 1.0
 * @return {JSX.Element} the display element
 * @constructor
 */
export function CoreFoodEntryViewer({ foodData, servingSize }: { foodData: FoodEntry, servingSize: number }) {
    return <div><table className="nutrientTable">
        <thead>
        <tr><th colSpan={3}>Nutritional Information</th></tr>
        <tr>
            <th>Nutrient</th>
            <th>Per Serving</th>
            <th>Per 100g</th>
        </tr>
        </thead>
        <tbody>
        {foodData.nutrientsInOrder().map(n =>
            <tr className={mapGroupToClazz(n.nutrient.group)}>
                <td>{n.nutrient.name}</td>
                <td>{(n.value * servingSize).toFixed(2)} {n.measurement.unit}</td>
                <td>{n.value.toFixed(2)} {n.measurement.unit}</td>
            </tr>
        )}
        </tbody>
    </table></div>;
}
